// TECH DEBT: typing is hard for this need time to fix
/* eslint-disable @typescript-eslint/no-explicit-any */

import { cn } from '@/lib/utils';
import { Separator } from '@radix-ui/react-separator';
import { X } from 'lucide-react';
import {
  type Dispatch,
  type SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type {
  CreatedDateFiltersState,
  DateFilterState,
  LastUpdatedDateFiltersState,
  PickupDateFiltersState,
} from '../types';

type BaseDateFilterChipProps<
  F extends
    | DateFilterState
    | PickupDateFiltersState
    | LastUpdatedDateFiltersState
    | CreatedDateFiltersState,
> = {
  filtersState: F;
  setFiltersState: Dispatch<SetStateAction<F>>;
  deleteFilter: () => void;
  FiltersComboBoxPopover: React.ComponentType<{
    comboBoxOnChange: (value: string) => void;
    comboBoxOpen: boolean;
    onOpenChange: (open: boolean) => void;
    filters: any;
    stayOpenAfterSelection: boolean;
    align: 'start' | 'end';
    popoverContentClassName: string;
    children: React.ReactNode;
  }>;
  representativeIcon: React.ReactNode;
  filterName: string;
  filterQuantifier: { singular: string; plural: string };
  shouldDeleteWhenNoFiltersAreSelected: boolean;
  hideFilterComboBoxPopover?: boolean;
  useBeforeAfterCopy?: boolean;
};

const BaseDateFilterChip = <
  // T extends "delivery_date" | "created_date" | "last_updated_date",
  F extends
    | DateFilterState
    | PickupDateFiltersState
    | LastUpdatedDateFiltersState
    | CreatedDateFiltersState,
>({
  filtersState,
  setFiltersState,
  deleteFilter,
  FiltersComboBoxPopover,
  hideFilterComboBoxPopover = false,
  representativeIcon,
  filterName,
  filterQuantifier,
  shouldDeleteWhenNoFiltersAreSelected,
  useBeforeAfterCopy = true,
}: BaseDateFilterChipProps<F>) => {
  const {
    selectedFilters: selectedFilters,
    filterMetaState: { isNegated },
  } = filtersState;

  const atLeastOneFilterWasSelected = useMemo(() => {
    return selectedFilters != 'none';
  }, [selectedFilters]);

  const negatedCopy = useMemo(() => {
    if (useBeforeAfterCopy) {
      return !isNegated ? 'before' : 'after';
    }

    return !isNegated ? 'is' : 'is not';
  }, [isNegated, useBeforeAfterCopy]);

  const [comboBoxIsOpen, setComboBoxIsOpen] = useState(false);

  // UX HACK: we'r using the `negatedCopyBeforeOpen` to measure ml-x in `popoverContentClassName`
  // so that the popover can be aligned, but also stable as things shift as statuses are selected
  const [negatedCopyBeforeOpen, setNegatedCopyBeforeOpen] =
    useState(negatedCopy);
  useEffect(() => {
    if (comboBoxIsOpen) {
      setNegatedCopyBeforeOpen(negatedCopy);
    }
  }, [comboBoxIsOpen]);

  const atLeastOneFilterWasSelectedRef = useRef(atLeastOneFilterWasSelected);
  useEffect(() => {
    // UX NOTE: once a chip has no filters at all just delet the filter
    if (
      atLeastOneFilterWasSelectedRef.current &&
      !atLeastOneFilterWasSelected &&
      shouldDeleteWhenNoFiltersAreSelected
    ) {
      deleteFilter();
    }
    atLeastOneFilterWasSelectedRef.current = atLeastOneFilterWasSelected;
  }, [atLeastOneFilterWasSelected]);

  return (
    <div>
      {atLeastOneFilterWasSelected && (
        <div className="flex flex-row w-fit bg-white text-gray-500 gap-2 h-fit border rounded-md p-0 items-center">
          <div className="flex flex-row gap-1 items-center">
            {representativeIcon}
            <span className="flex flex-row items-center">{filterName} </span>
            <Separator
              orientation="vertical"
              className="testing-sep w-[1px] h-[24px]  bg-gray-300"
            />

            <FiltersComboBoxPopover
              comboBoxOnChange={(value) => {
                setFiltersState((prev) => ({
                  ...prev,
                  selectedFilters: value,
                }));
              }}
              comboBoxOpen={comboBoxIsOpen}
              // TODO: might need to fix `onOpenChange`
              onOpenChange={setComboBoxIsOpen}
              filters={selectedFilters as any}
              stayOpenAfterSelection={true}
              align={'start'}
              popoverContentClassName={cn({
                // DEV NOTE: THIS IS A BRITTLE HACK
                // but aligning this is hard
                'ml-5': negatedCopyBeforeOpen == 'is',
                'ml-10': negatedCopyBeforeOpen == 'is not',
                'ml-14': negatedCopyBeforeOpen == 'is any of',
                'ml-11': negatedCopyBeforeOpen == 'before',
                'ml-8': negatedCopyBeforeOpen == 'after',
              })}
              // side
            >
              <div
                className="cursor-pointer"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setFiltersState((prev) => {
                    const currentIsNegated = prev.filterMetaState.isNegated;

                    return {
                      ...prev,
                      filterMetaState: {
                        ...prev.filterMetaState,
                        isNegated: !currentIsNegated,
                      },
                    };
                  });
                }}
              >
                {negatedCopy}
              </div>
            </FiltersComboBoxPopover>

            <Separator
              orientation="vertical"
              className="testing-sep w-[1px] h-[24px] bg-gray-300"
            />

            <div
              className={cn('flex flex-row', {
                'cursor-pointer': !hideFilterComboBoxPopover,
              })}
              onClick={() => setComboBoxIsOpen(true)}
            >
              <span>{filterQuantifier.singular}</span>
            </div>
          </div>

          <X
            className="size-4 cursor-pointer border rounded-md mr-1"
            onClick={deleteFilter}
          />
        </div>
      )}
    </div>
  );
};

export default BaseDateFilterChip;
